import React, { useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ReactECharts from 'echarts-for-react';
import OccupancyChart from '../components/OccupancyChart';
import PaymentSummaryAnalytics from '../components/PaymentSummaryAnalytics';
import LeaseRiskChart from '../components/LeaseRiskChart';
import { differenceInDays, parseISO, format } from 'date-fns';

const {
  FiAlertCircle, FiMail, FiTrendingUp, FiRefreshCw, FiClock, FiSettings,
  FiTrash2, FiX, FiHome, FiDollarSign, FiTool, FiCalendar, FiPieChart,
  FiBarChart2, FiActivity, FiShield, FiSliders, FiEye, FiEyeOff, FiRotateCcw
} = FiIcons;

// ─── Default widget config ───────────────────────────────────────────
const DEFAULT_WIDGETS = [
  { id: 'occupancy', label: 'Occupancy Trends', enabled: true },
  { id: 'payments', label: 'Payment Status', enabled: true },
  { id: 'leaseRisk', label: 'Lease Risk Forecast', enabled: true },
  { id: 'revenueByProperty', label: 'Revenue by Property', enabled: true },
  { id: 'renewals', label: 'Lease Renewals', enabled: true },
  { id: 'maintenance', label: 'Maintenance Overview', enabled: true },
  { id: 'recentActivity', label: 'Recent Activity', enabled: true },
  { id: 'tenantOverview', label: 'Tenant Overview', enabled: true },
];

function loadWidgetPrefs() {
  try {
    const saved = localStorage.getItem('dashboardWidgets');
    if (!saved) return DEFAULT_WIDGETS;
    const parsed = JSON.parse(saved);
    // Merge with defaults in case new widgets were added
    return DEFAULT_WIDGETS.map(dw => {
      const found = parsed.find(p => p.id === dw.id);
      return found ? { ...dw, enabled: found.enabled } : dw;
    });
  } catch { return DEFAULT_WIDGETS; }
}

// ─── Widget Card wrapper ─────────────────────────────────────────────
const WidgetCard = ({ title, icon, children, fullWidth }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden ${fullWidth ? 'lg:col-span-2' : ''}`}>
    <div className="px-5 py-3.5 border-b border-gray-50 flex items-center gap-2">
      <SafeIcon icon={icon} size={15} className="text-indigo-500" />
      <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ─── Revenue by Property chart ───────────────────────────────────────
const RevenueByPropertyChart = ({ properties }) => {
  const sorted = [...properties].sort((a, b) => (b.rentRate || 0) - (a.rentRate || 0));
  if (sorted.length === 0) return <p className="text-sm text-gray-400 text-center py-8">No properties yet.</p>;

  const option = {
    tooltip: { trigger: 'axis', formatter: (p) => `${p[0].name}: $${p[0].value.toLocaleString()}/mo` },
    grid: { left: '3%', right: '8%', bottom: '3%', top: '3%', containLabel: true },
    xAxis: { type: 'value', axisLabel: { formatter: '${value}', fontSize: 10 }, splitLine: { lineStyle: { type: 'dashed', color: '#f3f4f6' } } },
    yAxis: { type: 'category', data: sorted.map(p => p.name), axisLabel: { fontSize: 11 }, axisLine: { show: false }, axisTick: { show: false } },
    series: [{
      type: 'bar',
      data: sorted.map(p => p.rentRate || 0),
      barWidth: 18,
      itemStyle: { borderRadius: [0, 6, 6, 0], color: (params) => {
        const colors = ['#4f46e5', '#6366f1', '#818cf8', '#a5b4fc', '#c7d2fe'];
        return colors[params.dataIndex % colors.length];
      }},
    }]
  };
  return <ReactECharts option={option} style={{ height: Math.max(150, sorted.length * 40) + 'px' }} />;
};

// ─── Recent Activity feed ────────────────────────────────────────────
const RecentActivityFeed = ({ accessLogs, communications }) => {
  const items = [
    ...(accessLogs || []).slice(0, 10).map(l => ({ time: l.timestamp, text: l.event, type: 'log', user: l.userId })),
    ...(communications || []).slice(0, 10).map(c => ({ time: c.date, text: c.subject || `${c.type} to tenant`, type: 'comm' })),
  ].sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 8);

  if (items.length === 0) return <p className="text-sm text-gray-400 text-center py-8">No recent activity.</p>;

  return (
    <div className="space-y-2 max-h-[300px] overflow-y-auto">
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors">
          <div className={`mt-0.5 w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${item.type === 'log' ? 'bg-blue-50 text-blue-500' : 'bg-green-50 text-green-500'}`}>
            <SafeIcon icon={item.type === 'log' ? FiShield : FiMail} size={13} />
          </div>
          <div className="min-w-0">
            <p className="text-sm text-gray-800 truncate">{item.text}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">
              {item.time ? (() => { try { return format(new Date(item.time), 'MMM d, h:mm a'); } catch { return item.time; } })() : ''}
              {item.user ? ` · ${item.user}` : ''}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Tenant Overview ─────────────────────────────────────────────────
const TenantOverview = ({ tenants }) => {
  if (tenants.length === 0) return <p className="text-sm text-gray-400 text-center py-8">No tenants yet.</p>;
  const avgRating = (tenants.reduce((s, t) => s + (t.rating || 0), 0) / tenants.length).toFixed(1);
  const tagCounts = {};
  tenants.forEach(t => (t.tags || []).forEach(tag => { tagCounts[tag] = (tagCounts[tag] || 0) + 1; }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-indigo-50 rounded-lg p-3 text-center">
          <p className="text-xl font-bold text-indigo-700">{tenants.length}</p>
          <p className="text-[10px] font-semibold text-indigo-500 uppercase">Total</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-3 text-center">
          <p className="text-xl font-bold text-amber-700">{avgRating} <span className="text-amber-400 text-sm">★</span></p>
          <p className="text-[10px] font-semibold text-amber-500 uppercase">Avg Rating</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <p className="text-xl font-bold text-green-700">{tenants.filter(t => (t.tags || []).includes('Repeat Guest')).length}</p>
          <p className="text-[10px] font-semibold text-green-500 uppercase">Repeat</p>
        </div>
      </div>
      {Object.keys(tagCounts).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(tagCounts).sort((a, b) => b[1] - a[1]).map(([tag, count]) => (
            <span key={tag} className="px-2.5 py-1 bg-gray-100 text-gray-600 text-[10px] font-semibold rounded-full">
              {tag} ({count})
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD
// ═══════════════════════════════════════════════════════════════════════
const Dashboard = () => {
  const { data, clearAllData } = useStore();
  const [widgets, setWidgets] = useState(loadWidgetPrefs);
  const [showSettings, setShowSettings] = useState(false);
  const [showSampleBanner, setShowSampleBanner] = useState(() => !localStorage.getItem('sampleDataDismissed'));
  const [confirmClear, setConfirmClear] = useState(false);

  const hasSampleData = data.properties.some(p => p.id === 'p1' || p.id === 'p2');

  const handleClearData = () => {
    clearAllData();
    setShowSampleBanner(false);
    setConfirmClear(false);
    localStorage.setItem('sampleDataDismissed', 'true');
  };

  const toggleWidget = (id) => {
    const updated = widgets.map(w => w.id === id ? { ...w, enabled: !w.enabled } : w);
    setWidgets(updated);
    localStorage.setItem('dashboardWidgets', JSON.stringify(updated));
  };

  const resetWidgets = () => {
    setWidgets(DEFAULT_WIDGETS);
    localStorage.removeItem('dashboardWidgets');
  };

  const isEnabled = (id) => widgets.find(w => w.id === id)?.enabled;

  // ─── Data calculations ───────────────────────────────────────────
  const { properties, maintenance, leases, rentPayments, tenants, accessLogs, communications, recurringSchedules } = data;
  const today = new Date();

  const totalProps = properties.length;
  const occupied = properties.filter(p => p.status === 'Occupied').length;
  const occupancyRate = totalProps ? Math.round((occupied / totalProps) * 100) : 0;
  const pendingMaint = maintenance.filter(m => m.status === 'Pending' || m.status === 'In Progress').length;
  const monthlyRevenue = properties.reduce((sum, p) => sum + Number(p.rentRate || 0), 0);
  const overduePayments = (rentPayments || []).filter(p => p.status === 'Overdue').length;

  const upcomingExpirations = useMemo(() => {
    return properties.filter(p => {
      if (!p.leaseEnd) {
        const lease = leases.find(l => l.id === p.currentLeaseId);
        if (!lease?.endDate) return false;
        const days = differenceInDays(parseISO(lease.endDate), today);
        return days >= 0 && days <= 60;
      }
      const days = differenceInDays(parseISO(p.leaseEnd), today);
      return days >= 0 && days <= 60;
    }).length;
  }, [properties, leases, today]);

  const leaseAlerts = useMemo(() => {
    return properties.filter(p => {
      const lease = leases.find(l => l.id === p.currentLeaseId);
      return p.leaseEnd || lease?.endDate;
    }).map(p => {
      const lease = leases.find(l => l.id === p.currentLeaseId);
      const endDate = p.leaseEnd || lease?.endDate;
      const daysLeft = differenceInDays(parseISO(endDate), today);
      return { ...p, daysLeft, leaseEndDate: endDate };
    }).filter(p => p.daysLeft <= 60).sort((a, b) => a.daysLeft - b.daysLeft);
  }, [properties, leases, today]);

  const preventativeAlerts = useMemo(() => {
    return (recurringSchedules || []).map(s => {
      const daysUntilDue = differenceInDays(parseISO(s.nextDue), today);
      return { ...s, daysUntilDue };
    }).filter(s => s.daysUntilDue <= 7).sort((a, b) => a.daysUntilDue - b.daysUntilDue);
  }, [recurringSchedules, today]);

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <div className="space-y-5 p-6">
      {/* Sample data banner */}
      {showSampleBanner && hasSampleData && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
              <SafeIcon icon={FiAlertCircle} size={18} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-amber-900">You're viewing sample data</p>
              <p className="text-xs text-amber-700 mt-0.5">Explore the app with demo properties and tenants. When you're ready, clear it all to start fresh.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {confirmClear ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-700 font-medium whitespace-nowrap">Are you sure?</span>
                <button onClick={handleClearData} className="px-3 py-1.5 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transition-colors">Yes, clear all</button>
                <button onClick={() => setConfirmClear(false)} className="px-3 py-1.5 bg-white text-gray-600 text-xs font-medium rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors">Cancel</button>
              </div>
            ) : (
              <button onClick={() => setConfirmClear(true)} className="flex items-center gap-1.5 px-4 py-2 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700 transition-colors whitespace-nowrap">
                <SafeIcon icon={FiTrash2} size={13} /> Remove all sample data
              </button>
            )}
            <button onClick={() => { setShowSampleBanner(false); localStorage.setItem('sampleDataDismissed', 'true'); }} className="p-1.5 text-amber-400 hover:text-amber-600 transition-colors" title="Dismiss">
              <SafeIcon icon={FiX} size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Header + Customize button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
          <p className="text-sm text-gray-500">Portfolio overview and key metrics</p>
        </div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${showSettings ? 'bg-indigo-100 text-indigo-700' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
        >
          <SafeIcon icon={FiSliders} size={15} />
          Customize
        </button>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800">Dashboard Widgets</h3>
            <button onClick={resetWidgets} className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium">
              <SafeIcon icon={FiRotateCcw} size={12} /> Reset to defaults
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {widgets.map(w => (
              <button
                key={w.id}
                onClick={() => toggleWidget(w.id)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  w.enabled
                    ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                    : 'bg-gray-50 text-gray-400 border border-gray-100'
                }`}
              >
                <SafeIcon icon={w.enabled ? FiEye : FiEyeOff} size={14} />
                <span className="font-medium truncate">{w.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quick stats — always visible */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center"><SafeIcon icon={FiHome} size={16} className="text-indigo-600" /></div>
            {totalProps > 0 && <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">{occupied}/{totalProps}</span>}
          </div>
          <p className="text-2xl font-bold text-gray-900">{occupancyRate}%</p>
          <p className="text-xs text-gray-500 mt-0.5">Occupancy Rate</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center"><SafeIcon icon={FiDollarSign} size={16} className="text-green-600" /></div>
          </div>
          <p className="text-2xl font-bold text-gray-900">${monthlyRevenue.toLocaleString()}</p>
          <p className="text-xs text-gray-500 mt-0.5">Monthly Revenue</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${overduePayments > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
              <SafeIcon icon={FiAlertCircle} size={16} className={overduePayments > 0 ? 'text-red-500' : 'text-gray-400'} />
            </div>
            {overduePayments > 0 && <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Action needed</span>}
          </div>
          <p className="text-2xl font-bold text-gray-900">{overduePayments}</p>
          <p className="text-xs text-gray-500 mt-0.5">Overdue Payments</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${upcomingExpirations > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
              <SafeIcon icon={FiCalendar} size={16} className={upcomingExpirations > 0 ? 'text-amber-500' : 'text-gray-400'} />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">{upcomingExpirations}</p>
          <p className="text-xs text-gray-500 mt-0.5">Leases Expiring (60d)</p>
        </div>
      </div>

      {/* Preventative Maintenance Alert */}
      {preventativeAlerts.length > 0 && (
        <div className="bg-indigo-600 rounded-xl p-4 text-white flex items-center justify-between shadow-lg">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiSettings} size={22} />
            <div>
              <p className="font-bold text-sm">Preventative Maintenance Due</p>
              <p className="text-xs text-indigo-200">{preventativeAlerts[0].task} at {properties.find(p => p.id === preventativeAlerts[0].propertyId)?.name} — {preventativeAlerts[0].daysUntilDue <= 0 ? 'due now' : `in ${preventativeAlerts[0].daysUntilDue} days`}</p>
            </div>
          </div>
          <button onClick={() => window.location.hash = '#/operations'} className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg text-xs font-bold transition">View</button>
        </div>
      )}

      {/* Widget grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {isEnabled('occupancy') && (
          <WidgetCard title="Occupancy Trends" icon={FiTrendingUp} fullWidth>
            <OccupancyChart properties={properties} />
          </WidgetCard>
        )}

        {isEnabled('payments') && (
          <WidgetCard title="Payment Status" icon={FiPieChart}>
            {(rentPayments || []).length > 0 ? (
              <div className="h-[250px]">
                <ReactECharts
                  style={{ height: '100%' }}
                  option={{
                    tooltip: { trigger: 'item', formatter: '{b}: {c} ({d}%)' },
                    legend: { bottom: 0, left: 'center', textStyle: { fontSize: 10 }, icon: 'circle' },
                    series: [{
                      type: 'pie', radius: ['45%', '75%'],
                      itemStyle: { borderRadius: 8, borderColor: '#fff', borderWidth: 2 },
                      label: { show: false },
                      emphasis: { label: { show: true, fontSize: 12, fontWeight: 'bold' } },
                      data: [
                        { value: rentPayments.filter(p => p.status === 'Paid').length, name: 'Paid' },
                        { value: rentPayments.filter(p => p.status === 'Overdue').length, name: 'Overdue' },
                        { value: rentPayments.filter(p => p.status === 'Pending').length, name: 'Pending' },
                      ],
                      color: ['#10B981', '#EF4444', '#6366f1']
                    }]
                  }}
                />
              </div>
            ) : <p className="text-sm text-gray-400 text-center py-12">No payment data yet.</p>}
          </WidgetCard>
        )}

        {isEnabled('leaseRisk') && (
          <WidgetCard title="Lease Risk Forecast" icon={FiBarChart2}>
            <LeaseRiskChart properties={properties} leases={leases} />
          </WidgetCard>
        )}

        {isEnabled('revenueByProperty') && (
          <WidgetCard title="Revenue by Property" icon={FiDollarSign}>
            <RevenueByPropertyChart properties={properties} />
          </WidgetCard>
        )}

        {isEnabled('renewals') && (
          <WidgetCard title="Lease Renewals" icon={FiClock}>
            <div className="divide-y divide-gray-100 max-h-[320px] overflow-y-auto -mx-5 -mb-5">
              {leaseAlerts.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-12 px-5">No upcoming lease expirations.</p>
              ) : (
                leaseAlerts.map(p => (
                  <div key={p.id} className="px-5 py-3.5 hover:bg-gray-50 transition-colors flex items-center justify-between">
                    <div className="flex items-start gap-3">
                      <div className={`mt-0.5 p-1.5 rounded-lg ${p.daysLeft < 0 ? 'bg-black text-white' : p.daysLeft < 15 ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>
                        <SafeIcon icon={FiAlertCircle} size={14} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{p.name}</p>
                        <p className="text-xs text-gray-500">{p.tenantName}</p>
                        <p className={`text-[10px] font-bold mt-0.5 ${p.daysLeft < 0 ? 'text-black' : p.daysLeft < 15 ? 'text-red-600' : 'text-orange-600'}`}>
                          {p.daysLeft < 0 ? `Expired ${Math.abs(p.daysLeft)}d ago` : `${p.daysLeft}d remaining`}
                        </p>
                      </div>
                    </div>
                    <button title="Send Renewal Notice" className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition" onClick={() => window.location.href = `mailto:${p.tenantEmail}?subject=Lease Renewal - ${p.name}`}>
                      <SafeIcon icon={FiMail} size={16} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </WidgetCard>
        )}

        {isEnabled('maintenance') && (
          <WidgetCard title="Maintenance Overview" icon={FiTool}>
            <div className="divide-y divide-gray-100 -mx-5 -mb-5">
              {maintenance.filter(m => m.status !== 'Completed').length === 0 && preventativeAlerts.length === 0 ? (
                <div className="text-center py-12 px-5">
                  <p className="text-sm text-gray-400">Portfolio is healthy</p>
                  <p className="text-xs text-gray-300 mt-1">No pending maintenance issues</p>
                </div>
              ) : (
                <>
                  {preventativeAlerts.slice(0, 3).map(s => (
                    <div key={s.id} className="px-5 py-3.5 flex items-center justify-between bg-indigo-50/30">
                      <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-indigo-100 text-indigo-600 rounded-lg"><SafeIcon icon={FiRefreshCw} size={13} /></div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{s.task}</p>
                          <p className="text-[10px] text-indigo-600 font-semibold uppercase">Scheduled</p>
                        </div>
                      </div>
                      <span className="text-xs font-semibold text-indigo-700">{s.nextDue}</span>
                    </div>
                  ))}
                  {maintenance.filter(m => m.status !== 'Completed').slice(0, 4).map(m => (
                    <div key={m.id} className="px-5 py-3.5 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{m.issue}</p>
                        <p className="text-xs text-gray-500">{properties.find(p => p.id === m.propertyId)?.name}</p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${m.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>{m.status}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          </WidgetCard>
        )}

        {isEnabled('recentActivity') && (
          <WidgetCard title="Recent Activity" icon={FiActivity}>
            <RecentActivityFeed accessLogs={accessLogs} communications={communications} />
          </WidgetCard>
        )}

        {isEnabled('tenantOverview') && (
          <WidgetCard title="Tenant Overview" icon={FiHome}>
            <TenantOverview tenants={tenants || []} />
          </WidgetCard>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
